import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiCalendar, FiMapPin, FiGithub, FiCheckCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import api from '../../services/api';
import Toast from '../../components/Toast';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const LIMIT = 10;

  useEffect(() => {
    if (userId) fetchUserAssignments(1);
  }, [userId]);

  const fetchUserAssignments = async (page) => {
    try {
      setLoading(true);
      const response = await api.post(`/users/${userId}/assignments`, { page, limit: LIMIT });
      setData(response.data);
      setPagination(response.data.pagination);
      setCurrentPage(page);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching user assignments:', err);
      setError('Failed to load user details: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      todo: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      testing: 'bg-purple-100 text-purple-800',
      client_review: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const statusLabels = {
      todo: 'To Do',
      in_progress: 'In Progress',
      testing: 'Testing',
      client_review: 'Client Review',
      completed: 'Completed'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={() => fetchUserAssignments(1)} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!data || !data.user) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">User not found</div>
        <button 
          onClick={() => navigate(-1)} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { user, tasks } = data;

  return (
    <div className="p-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FiArrowLeft className="mr-2" /> Back to Team
        </button>
      </div>

      {/* User Info */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-medium text-xl">
              {user.name?.split(' ').map(n => n[0]).join('') || 'U'}
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-semibold text-gray-900">{user.name}</h1>
              <p className="text-sm text-gray-500 capitalize">{user.role}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiMail className="mr-2" />
                <span>{user.email}</span>
              </div>
              
              {user.mobile_number && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiPhone className="mr-2" />
                  <span>{user.mobile_number}</span>
                </div>
              )}
              
              {user.github_url && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiGithub className="mr-2" />
                  <a href={user.github_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    GitHub Profile
                  </a>
                </div>
              )}
            </div>
            
            <div>
              {user.joining_date && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiCalendar className="mr-2" />
                  <span>Joined: {formatDate(user.joining_date)}</span>
                </div>
              )}
              
              {user.city && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiMapPin className="mr-2" />
                  <span>{user.city}</span>
                </div>
              )}
              
              {user.address && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Address: </span>
                  <span>{user.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-6">
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <FiCheckCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Assigned Tasks</p>
              <p className="text-2xl font-semibold text-gray-900">{data?.taskCount || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-lg font-medium text-gray-900">Assigned Tasks ({data?.taskCount || 0})</h2>
        </div>
        
        <div className="overflow-x-auto">
          {tasks.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link 
                        to={`/admin/tasks/${task.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                      >
                        {task.title}
                      </Link>
                      {task.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{task.description ? task.description.replace(/<[^>]*>/g, ' ').trim() : ''}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(task.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(task.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task.Project?.name || 'No project'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task.Client?.name || 'No client'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(task.due_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-500">No tasks assigned</div>
          )}
        </div>
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * LIMIT + 1}–{Math.min(currentPage * LIMIT, pagination.totalItems)} of {pagination.totalItems}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => fetchUserAssignments(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => fetchUserAssignments(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    page === currentPage ? 'bg-blue-500 text-white border-blue-500' : 'hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => fetchUserAssignments(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailPage;